import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Save, FileDown, Check, X, Send, Copy, Trash2, ArrowLeft, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { lineTotal, money, customerName } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";
import { LineItems } from "../components/quote/LineItems";
import { DesignsPanel } from "../components/designer/DesignsPanel";
import { DatePicker } from "../components/DatePicker";
import { QuoteJobCard } from "../components/quote/QuoteJobCard";
import { CustomerDialog } from "../components/CustomerDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const EMPTY = { customer_id: "", title: "", site_address: "", notes: "", items: [], designs: [], status: "draft", valid_until: "" };

const siteOf = (c) => (c.site_same_as_billing ? [c.street, c.suburb, c.state, c.postcode] : [c.site_street, c.site_suburb, c.site_state, c.site_postcode]).filter(Boolean).join(", ");

export default function QuoteEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState({ ...EMPTY, customer_id: params.get("customer") || "" });
  const [customers, setCustomers] = useState([]);
  const [gst, setGst] = useState({ rate: 10, on: true });
  const [busy, setBusy] = useState(false);
  const [custOpen, setCustOpen] = useState(false);
  const [tab, setTab] = useState("items");
  const [draft, setDraft] = useState(null);
  const isNew = !id;

  useEffect(() => {
    api.get("/customers").then((r) => {
      setCustomers(r.data);
      const c = !id && r.data.find((x) => x.id === params.get("customer"));
      if (c) setQ((p) => ({ ...p, customer_id: c.id, site_address: siteOf(c) }));
    });
    api.get("/settings").then((r) => setGst({ rate: r.data.gst_rate, on: r.data.gst_registered }));
    if (id) api.get(`/quotes/${id}`).then((r) => setQ(r.data)).catch(() => navigate("/quotes"));
  }, [id, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setQ((p) => ({ ...p, [k]: v }));
  const totals = useMemo(() => {
    const sub = q.items.reduce((s, i) => s + lineTotal(i), 0);
    const g = gst.on ? (sub * gst.rate) / 100 : 0;
    return { sub, g, total: sub + g };
  }, [q.items, gst]);
  const customer = customers.find((c) => c.id === q.customer_id);

  const onCustomerChange = (cid) => {
    const c = customers.find((x) => x.id === cid);
    setQ((p) => {
      const next = { ...p, customer_id: cid };
      if (c && !p.site_address) {
        next.site_address = siteOf(c);
      }
      return next;
    });
  };

  const save = async (extra = {}) => {
    if (!q.customer_id) { toast.error("Please select a customer"); return null; }
    if (draft) { setTab("design"); toast.error("Finish the open design first — click 'Add to quote' or Cancel"); return null; }
    setBusy(true);
    try {
      const body = { customer_id: q.customer_id, title: q.title, site_address: q.site_address, notes: q.notes, items: q.items, designs: q.designs, status: q.status, valid_until: q.valid_until, ...extra };
      const r = isNew ? await api.post("/quotes", body) : await api.put(`/quotes/${id}`, body);
      setQ(r.data.customer !== undefined ? r.data : { ...r.data });
      if (isNew) navigate(`/quotes/${r.data.id}`, { replace: true });
      return r.data;
    } catch (e) { toast.error(errMsg(e)); return null; } finally { setBusy(false); }
  };

  const onSave = async () => { if (await save()) toast.success("Quote saved"); };
  const setStatus = async (status) => { if (await save({ status })) toast.success(`Quote marked as ${status}`); };

  const exportPdf = async () => {
    const saved = await save();
    if (!saved) return;
    try {
      const r = await api.get(`/quotes/${saved.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${saved.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF export failed"); }
  };

  const duplicate = async () => {
    const r = await api.post(`/quotes/${id}/duplicate`);
    toast.success("Quote duplicated");
    navigate(`/quotes/${r.data.id}`);
  };
  const remove = async () => {
    if (!window.confirm(`Delete quote ${q.number}?`)) return;
    try { await api.delete(`/quotes/${id}`); } catch (e) { return toast.error(errMsg(e)); }
    toast.success("Quote deleted");
    navigate("/quotes");
  };

  return (
    <div className="animate-rise">
      <button data-testid="back-to-quotes" onClick={() => navigate("/quotes")} className="mb-4 flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900"><ArrowLeft className="h-4 w-4" />All quotes</button>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-1">{isNew ? "New quote" : `Quote · ${customerName(customer)}`}</div>
          <div className="flex items-center gap-3">
            <h1 data-testid="quote-number" className="font-display text-4xl font-bold uppercase tracking-tight sm:text-5xl">{q.number || "Draft"}</h1>
            <StatusBadge status={q.status} testId="quote-status-badge" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isNew && <>
            <Button data-testid="duplicate-quote-button" variant="outline" onClick={duplicate}><Copy className="mr-1 h-4 w-4" />Duplicate</Button>
            <Button data-testid="delete-quote-button" variant="outline" onClick={remove} className="text-red-600"><Trash2 className="mr-1 h-4 w-4" />Delete</Button>
          </>}
          <Button data-testid="export-pdf-button" variant="outline" disabled={busy} onClick={exportPdf}><FileDown className="mr-1 h-4 w-4" />Export PDF</Button>
          <Button data-testid="save-quote-button" disabled={busy} onClick={onSave} className="bg-red-600 hover:bg-red-700"><Save className="mr-1 h-4 w-4" />{busy ? "Saving…" : "Save quote"}</Button>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-6">
          <div className="grid gap-4 rounded-lg border border-zinc-200 bg-white p-5 md:grid-cols-2">
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-zinc-500">Customer *</Label>
              <div className="flex gap-2">
                <select data-testid="quote-customer-select" value={q.customer_id} onChange={(e) => onCustomerChange(e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                  <option value="">Select a customer…</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{customerName(c)}{c.company_name ? ` (${c.contact_name})` : ""}</option>)}
                </select>
                <Button data-testid="quote-new-customer" type="button" variant="outline" size="icon" title="New customer" onClick={() => setCustOpen(true)}><UserPlus className="h-4 w-4" /></Button>
              </div>
            </div>
            <div><Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-zinc-500">Title / job</Label><Input data-testid="quote-title-input" value={q.title} placeholder="e.g. Kitchen fit-out – dishwash area" onChange={(e) => set("title", e.target.value)} /></div>
            <div><Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-zinc-500">Site address</Label><Input data-testid="quote-site-input" value={q.site_address} onChange={(e) => set("site_address", e.target.value)} /></div>
            <div><Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-zinc-500">Valid until</Label><DatePicker testId="quote-valid-until-input" value={q.valid_until} onChange={(v) => set("valid_until", v)} /></div>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-zinc-200/60">
              <TabsTrigger data-testid="tab-items" value="items">Items & Pricing ({q.items.length})</TabsTrigger>
              <TabsTrigger data-testid="tab-design" value="design">Designs ({q.designs.length})</TabsTrigger>
              <TabsTrigger data-testid="tab-notes" value="notes">Notes</TabsTrigger>
            </TabsList>
            <TabsContent value="items" className="mt-4"><LineItems items={q.items} onChange={(items) => set("items", items)} /></TabsContent>
            <TabsContent value="design" className="mt-4">
              <DesignsPanel designs={q.designs} draft={draft} setDraft={setDraft} onChange={(d) => set("designs", d)} onAddLineItem={(li) => setQ((p) => ({ ...p, items: [...p.items, li] }))} />
            </TabsContent>
            <TabsContent value="notes" className="mt-4">
              <Textarea data-testid="quote-notes-input" rows={8} className="bg-white" placeholder="Scope of works, exclusions, notes shown on the PDF…" value={q.notes} onChange={(e) => set("notes", e.target.value)} />
            </TabsContent>
          </Tabs>
        </div>

        <aside className="space-y-4 xl:sticky xl:top-24 xl:self-start">
          <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="quote-totals">
            <div className="eyebrow mb-3">Summary</div>
            <div className="flex justify-between py-1.5 text-sm"><span className="text-zinc-500">Subtotal (ex GST)</span><span data-testid="quote-subtotal" className="font-mono">{money(totals.sub)}</span></div>
            <div className="flex justify-between py-1.5 text-sm"><span className="text-zinc-500">GST ({gst.on ? gst.rate : 0}%)</span><span data-testid="quote-gst" className="font-mono">{money(totals.g)}</span></div>
            <div className="mt-2 flex items-baseline justify-between border-t-2 border-red-600 pt-3"><span className="font-display text-lg font-bold uppercase">Total</span><span data-testid="quote-total" className="font-mono text-2xl font-bold">{money(totals.total)}</span></div>
            <div className="mt-1 text-right font-mono text-[10px] uppercase tracking-wider text-zinc-400">AUD inc GST</div>
          </div>
          <div className="rounded-lg border border-zinc-200 bg-white p-5">
            <div className="eyebrow mb-3">Outcome</div>
            <div className="grid grid-cols-2 gap-2">
              <Button data-testid="approve-quote-button" disabled={busy} onClick={() => setStatus("approved")} className="bg-green-600 hover:bg-green-700"><Check className="mr-1 h-4 w-4" />Approve</Button>
              <Button data-testid="decline-quote-button" disabled={busy} onClick={() => setStatus("declined")} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50"><X className="mr-1 h-4 w-4" />Decline</Button>
              <Button data-testid="mark-sent-button" disabled={busy} onClick={() => setStatus("sent")} variant="outline"><Send className="mr-1 h-4 w-4" />Mark sent</Button>
              <Button data-testid="mark-draft-button" disabled={busy} onClick={() => setStatus("draft")} variant="ghost">Reset to draft</Button>
            </div>
          </div>
          <QuoteJobCard key={`${id}-${q.status}`} quoteId={id} quoteStatus={q.status} />
        </aside>
      </div>
      <CustomerDialog open={custOpen} onOpenChange={setCustOpen} customer={null}
        onSaved={(c) => { setCustomers((p) => [c, ...p]); onCustomerChange(c.id); }} />
    </div>
  );
}
